import { Moon, Sun } from "@phosphor-icons/react";
import { useTheme } from "./theme-provider";
import { Tooltip } from "./Tooltip";
import { cn } from "./cn";

const OPTION =
  "rounded-full p-2 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

/**
 * The light / dark / system switch the sign-in pages use: three round icon
 * buttons in a pill, the current one raised. Reads and sets the theme through
 * `useTheme`, so it works anywhere under the theme provider.
 */
export function ThemeSelector({
  className,
  size = "md",
}: {
  className?: string;
  /** `sm` is for dense bars such as Studio's status bar. */
  size?: "md" | "sm";
}) {
  const small = size === "sm";
  const { theme, setTheme } = useTheme();
  const options = [
    {
      value: "system" as const,
      label: "System theme",
      icon: (
        <svg
          className={small ? "h-3 w-3" : "h-4 w-4"}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <rect x="2" y="3" width="20" height="14" rx="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
        </svg>
      ),
    },
    {
      value: "light" as const,
      label: "Light theme",
      icon: <Sun className={small ? "h-3 w-3" : "h-4 w-4"} />,
    },
    {
      value: "dark" as const,
      label: "Dark theme",
      icon: <Moon className={small ? "h-3 w-3" : "h-4 w-4"} />,
    },
  ];

  return (
    <div
      role="group"
      aria-label="Theme"
      className={cn(
        small
          ? "flex items-center gap-0.5 rounded-full p-0.5"
          : "flex items-center gap-1 rounded-full p-1",
        theme === "dark" ? "bg-[#2a2a2a]" : "bg-muted",
        className
      )}
    >
      {options.map(({ value, label, icon }) => (
        <Tooltip key={value} content={label} side="top">
          <button
            type="button"
            onClick={() => setTheme(value)}
            aria-label={label}
            aria-pressed={theme === value}
            className={cn(
              small
                ? "rounded-full p-1 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                : OPTION,
              theme === value
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {icon}
          </button>
        </Tooltip>
      ))}
    </div>
  );
}
